import type { RefObject } from 'react';
import { useEffect, useRef } from 'react';

type ClickOutsideHandler = (event: MouseEvent) => void;

const useClickOutside = (ref: RefObject<HTMLDivElement>, onClickOutside: ClickOutsideHandler) => {
  const savedCallback = useRef<ClickOutsideHandler>();

  // Save the provided onClickOutside function to the ref
  useEffect(() => {
    savedCallback.current = onClickOutside;
  }, [onClickOutside]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Check if the click target is outside the provided ref (div component)
      if (ref.current && !ref.current.contains(event.target as Node)) {
        // Check if the target is a descendant of the provided ref
        let targetElement = event.target as Node | null;
        let clickedOutside = true;

        while (targetElement !== null) {
          if (targetElement === ref.current) {
            clickedOutside = false;
            break;
          }
          targetElement = targetElement.parentNode;
        }

        // Trigger the onClickOutside function if it exists
        if (clickedOutside && typeof savedCallback.current === 'function') {
          savedCallback.current(event);
        }
      }
    };

    // Attach the event listener to the document
    document.addEventListener('click', handleClickOutside, { passive: true });

    // Clean up the event listener when the component unmounts
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [ref]);
};

export default useClickOutside;
